import { Game } from "../game.js";
import { isNetworkishError } from "../utils/network_errors.js";
import { Screen } from "./screen.js";
import { preloadAdsInBackground } from "../ads/first_load_manager.js";
import { audioController } from "../audio/audio_controller.js";
import { AudioClips } from "../audio/audio_constants.js";
import { WebGLUIRuntime } from "../webgl_2d/webgl_ui_runtime.js";
import { Vfx2DRuntime } from "../webgl_2d/vfx_2d_runtime.js";
import { LevelPackService, OfflineLevelUnavailableError } from "../level_pack/level_pack_service.js";
import { ConveyorLevelsService } from "../conveyor_config/conveyor_levels_service.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { NetworkManager } from "../network_manager.js";
import { Vfx2DManager } from "../webgl_2d/vfx_2d_manager.js";
import { Vector2 } from "../common/vector2.js";
import { isMemoryConstrained } from "../utils/memory_policy.js";
import type { LoadingGameVariant } from "../ui/screens/loading_game_screen.ts/loading_game_screen.js";

export enum Screens
{
  GAME = "game",
  MENU = "menu",
  LEADERBOARD = "leaderboard",
  FRIENDS = "friends",
  HAPPY_FALL = "happy_fall",
}

export class ScreenManager
{
  private static canvases: Record<Screens, HTMLCanvasElement[]> = {} as any;

  private static current_screen: Screen | null = null;
  private static current_ui: any;
  private static current_screen_name: Screens | null = null;
  private static change_listeners: Array<( next: Screens | null ) => void> = [];
  private static after_next_go_back_callback: (() => void) | null = null;

  private static needed_navigation_block: Record<Screens, boolean> = {} as any;
  private static readonly COIN_IMPACT_TAIL_MS = 250;

  static registerCanvas( name: Screens, canvas: HTMLCanvasElement | null, is_navigation_block: boolean ): void
  {
    if ( !canvas )
      throw new Error( `[ScreenManager] Canvas is missing for screen "${name}".` );

    if ( !this.canvases[name] )
      this.canvases[name] = [];

    this.canvases[name].push( canvas );
    canvas.style.display = "none";

    this.needed_navigation_block[name] = is_navigation_block;
  }

  static registerMenuScreenCanvases( canvases: ( HTMLCanvasElement | null )[], is_navigation_block: boolean ): void
  {
    for ( const canvas of canvases )
    {
      if ( canvas )
      {
        this.registerCanvas( Screens.MENU, canvas, is_navigation_block );
      }
    }
  }

  static registerGameScreenCanvases( canvases: ( HTMLCanvasElement | null )[], is_navigation_block: boolean = false ): void
  {
    for ( const canvas of canvases )
    {
      if ( canvas )
      {
        this.registerCanvas( Screens.GAME, canvas, is_navigation_block );
      }
    }
  }

  public static isMenuTab( s: Screens | null | undefined ): boolean
  {
    return s === Screens.MENU;
  }

  static getCurrentScreen(): Screens | null
  {
    return this.current_screen_name;
  }

  static getCurrentScreenInstance(): Screen | null
  {
    return this.current_screen;
  }

  /** Sends a complete tap directly to the currently active canvas UI. */
  public static replayTapOnCurrentUi( p: Vector2, source_canvas?: HTMLCanvasElement ): void
  {
    const ui = this.current_ui;
    if ( !ui || typeof ui.onTouchStart !== "function" || typeof ui.onTouchEnd !== "function" )
      return;

    let target_p = p;
    const target_canvas = typeof ui.getContext === "function"
      ? ui.getContext()?.canvas as HTMLCanvasElement | undefined
      : undefined;
    if ( source_canvas && target_canvas )
    {
      const source_rect = source_canvas.getBoundingClientRect();
      const target_rect = target_canvas.getBoundingClientRect();
      target_p = new Vector2(
        source_rect.left + p.x - target_rect.left,
        source_rect.top + p.y - target_rect.top
      );
    }

    console.log( "[TooltipTapDebug] screen replay mapping", {
      current_screen: this.current_screen_name,
      current_ui: typeof ui.name === "function" ? ui.name() : ui.constructor?.name,
      source_point: { x: p.x, y: p.y },
      target_point: { x: target_p.x, y: target_p.y },
      source_canvas: source_canvas?.id ?? null,
      source_rect: source_canvas ? this.debugCanvasRect( source_canvas ) : null,
      target_canvas: target_canvas?.id ?? null,
      target_rect: target_canvas ? this.debugCanvasRect( target_canvas ) : null,
      hit_path: typeof ui.debugTouchHitPath === "function"
        ? ui.debugTouchHitPath( target_p )
        : "unavailable",
    } );

    const now = performance.now();
    const handled = ui.onTouchStart( target_p, now );
    console.log( "[TooltipTapDebug] screen replay down result", { handled } );
    ui.onTouchEnd( target_p, now );
    console.log( "[TooltipTapDebug] screen replay completed", {
      current_screen_after: this.current_screen_name,
    } );
  }

  private static debugCanvasRect( canvas: HTMLCanvasElement ): Record<string, number>
  {
    const rect = canvas.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      buffer_width: canvas.width,
      buffer_height: canvas.height,
    };
  }

  static addOnChangeListener( listener: ( next: Screens | null ) => void ): void
  {
    this.change_listeners.push( listener );
  }

  static setAfterNextGoBackCallback( callback: (() => void) | null ): void
  {
    this.after_next_go_back_callback = callback;
  }

  private static notifyScreenChanged(): void
  {
    for ( const listener of this.change_listeners )
    {
      try { listener( this.current_screen_name ); }
      catch ( e ) { console.warn( '[ScreenManager] change listener failed', e ); }
    }
  }

  static hideAll( preserve_vfx: boolean = false )
  {
    if ( !preserve_vfx )
    {
      WebGLUIRuntime.clearAll()
      Vfx2DRuntime.clearAll();
    }
    
    const release = isMemoryConstrained();
    for ( const canvas_list of Object.values( this.canvases ) )
    {
      for ( const canvas of canvas_list )
      {
        canvas.style.display = "none";
        if ( release )
          Game.instance.releaseScreenCanvas( canvas );
      }
    }
  }

  static async showScreen( name: Screens, opts?: any )
  {
    // The menu is intentionally usable while startup network data loads, but
    // a level must not be constructed until its release is ready. Otherwise
    // the first entry uses the bundled layout and a repeated entry uses the
    // server layout for the very same level number.
    if ( name === Screens.GAME )
      await LevelPackService.waitForStartupReady();

    if ( name === Screens.GAME && !opts?.is_conveyor && !NetworkManager.isOnline()
      && LevelPackService.isActive() && !LevelPackService.isLevelCached( opts?.level ?? 1 ) )
    {
      NetworkManager.showOfflineLevelsExhausted();
      return;
    }

    if ( name === Screens.LEADERBOARD && !NetworkManager.isOnline() )
    {
      NetworkManager.showOfflineLeaderboard();
      return;
    }

    // A level-to-level transition is still a full game-screen rebuild, so it
    // needs the loading canvas just as much as a menu-to-game transition.
    const manages_game_loading = name === Screens.GAME
      && !Game.instance.isLoadingGameScreenShown();

    if ( manages_game_loading )
    {
      Game.instance.showLoadingGameScreen( opts?.preserve_vfx === true, ScreenManager.loadingVariant( opts ) );
      Game.instance.holdSceneRevealUntil( Vfx2DManager.getLatestCoinFlyFinishTime() + ScreenManager.COIN_IMPACT_TAIL_MS );
      await Game.instance.waitForLoadingGameScreenPaint();
    }

    if ( name === Screens.GAME )
      await ResourceManager.whenReady();

    try
    {
      if ( name === Screens.GAME && !opts?.is_conveyor )
      {
        try
        {
          await LevelPackService.ensureLevel( opts?.level ?? 1 );
        }
        catch ( e )
        {
          console.warn( "[ScreenManager] level fetch failed, staying on the current screen", e );
          if ( !( e instanceof OfflineLevelUnavailableError ) && isNetworkishError( e ) )
            NetworkManager.handleNetworkError( e );
          NetworkManager.showOfflineLevelsExhausted();
          if ( manages_game_loading && this.current_screen_name !== Screens.GAME )
            await this.showScreenContent( this.current_screen_name ?? Screens.MENU );
          return;
        }
      }

      if ( name === Screens.GAME && opts?.is_conveyor && NetworkManager.isOnline() )
        void ConveyorLevelsService.refresh();

      await this.showScreenContent( name, opts );
    }
    finally
    {
      if ( manages_game_loading )
      {
        await Game.instance.waitForSceneRevealed();
        await Game.instance.waitForMinimumLoadingGameScreenDuration();
        if ( this.current_screen_name === Screens.GAME )
          await this.current_screen?.beforeLoadingHidden?.( () => Game.instance.waitForLoadingGamePlay() );
        Game.instance.hideLoadingGameScreen();
      }
    }
  }

  private static loadingVariant( opts?: any ): LoadingGameVariant
  {
    return opts?.is_conveyor ? "conveyor" : "default";
  }

  private static async showScreenContent( name: Screens, opts?: any ): Promise<void>
  {
    await Game.instance.prepareScreenImages( name );

    this.hideAll( opts?.preserve_vfx === true );

    Game.instance.setGameSceneCanvasVisible( name === Screens.GAME );

    const ui_canvases = this.canvases[name];

    if ( ui_canvases )
    {
      for ( const canvas of ui_canvases )
      {
        Game.instance.allocateScreenCanvas( canvas );
        canvas.style.display = "block";
      }
    }

    if ( this.current_screen )
    {
      this.current_screen.destroy();
      this.current_screen = null;
    }

    if ( this.current_ui )
    {
      if ( typeof this.current_ui.destroy === "function" )
        this.current_ui.destroy();

      if ( typeof this.current_ui.cleanup === "function" )
        this.current_ui.cleanup();

      this.current_ui = null;
    }

    Game.instance.forgetScreens();

    // Set the active screen name before syncing the navigation bar: its
    // updateScreenBlock() reads getCurrentScreen(), so it must already reflect
    // the destination or the bar keeps highlighting the previous tab (e.g. a
    // dialog redirecting to the shop left "Menu" selected).
    this.current_screen_name = name;

    Game.instance.updateNavigationBlock();

    if ( this.needed_navigation_block[name] )
    {
      Game.instance.showNavigationBlock();
    } else
    {
      Game.instance.hideNavigationBlock();
    }

    if ( name === Screens.GAME )
    {
      const { screen, ui } = Game.instance.initializeGameScreen();

      this.current_screen = screen;
      this.current_ui = ui;
    }

    if ( name === Screens.MENU )
    {
      this.current_ui = Game.instance.initializeMenuScreen();
      preloadAdsInBackground();
    }

    if ( name === Screens.LEADERBOARD )
    {
      this.current_ui = Game.instance.initializeLeaderboardScreen();
    }

    if ( name === Screens.FRIENDS )
    {
      this.current_ui = Game.instance.initializeFriendsScreen();
    }

    if ( name === Screens.HAPPY_FALL )
    {
      this.current_ui = Game.instance.initializeHappyFallScreen();
    }

    Game.instance.releaseScreenImages( name );

    this.notifyScreenChanged();
    void ( name === Screens.GAME && opts?.is_conveyor === true
      ? audioController.playEscalatorMusic()
      : audioController.playMainMusic() );

    if ( !this.current_screen )
      return;

    const screen = this.current_screen;

    await screen.init( {
      level: opts?.level ?? 1,
      is_conveyor: opts?.is_conveyor === true,
      level_start_booster_id: opts?.level_start_booster_id,
      defer_level_start_booster: opts?.defer_level_start_booster === true,
    } );

    if ( this.current_screen !== screen )
      return;

    // Keep the loading canvas above the scene until the initialized game UI
    // has produced a real frame. Otherwise the WebGL clear/background can be
    // exposed for one or more frames between init() and the render loop.
    if ( name === Screens.GAME )
      await Game.instance.waitForNextGameUIPaint();

    // Do not await: startup effects (e.g. level-start mines) must start only
    // after the loading canvas is removed, so the player sees them fly.
    // The screen owns input blocking while this runs.
    void Game.instance.waitForLoadingGameScreenHidden().then( () => screen.afterFirstPaint?.() );
  }

  static showPreparedScreen(
    name: Screens,
    screen?: Screen,
    ui?: any
  )
  {
    this.hideAll();

    Game.instance.setGameSceneCanvasVisible( name === Screens.GAME );

    const ui_canvases = this.canvases[name];

    if ( ui_canvases )
    {
      for ( const canvas of ui_canvases )
      {
        Game.instance.allocateScreenCanvas( canvas );
        canvas.style.display = "block";
      }
    }

    this.current_screen = screen ?? null;
    this.current_ui = ui ?? null;
    this.current_screen_name = name;
    this.notifyScreenChanged();
    void audioController.playMainMusic();
  }

  static update( dt: number )
  {
    if ( this.current_screen )
      this.current_screen.update( dt );
  }
}
